/**
 * use-permissions — TanStack Query hooks dla F10 T10.10 (panel klienta — Settings).
 *
 * - usePermissionDefinitions — slownik uprawnien (filtrowany do aktywnych modulow tenanta).
 * - useCompanies — lista firm tenanta (do pickera "Dodaj firme").
 * - useUsersList — lista userow tenanta z chipami firm.
 * - useUserPermissions — per-firma role + dosypane uprawnienia dla wybranego usera.
 * - useCompanyMembers — odwrotny widok: userzy + role dla wybranej firmy (widget
 *   "Uzytkownicy firmy" na karcie firmy).
 * - useRoleTemplates — szablony rol tenanta (4 systemowe + custom).
 *
 * Wszystkie staleTime 30s (default z createQueryClient to 60s, ale tu chcemy szybciej
 * widziec efekt zapisow per-wiersz auto-save).
 */

import { useQuery } from '@tanstack/react-query';

import type { ListResponseMeta } from '@/components/configurable-table/useServerListControls';
import { api } from '@/lib/api';
import type { ExtRefLabels } from '@/lib/ext-list-columns';

// ============================================================================
// Types
// ============================================================================

export interface PermissionDefinition {
  code: string;
  moduleCode: string;
  name: string | null;
  description: string | null;
  category: string | null;
  scopeAllowed: string[] | null;
  isAdminOnly: boolean;
  sortOrder: number | null;
}

export interface PermissionDefinitionsResponse {
  definitions: PermissionDefinition[];
  grouped: Record<string, PermissionDefinition[]>;
}

export interface CompanyListItem {
  id: string;
  legalName: string;
  shortName: string | null;
  taxId: string | null;
  isPrimary: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface CompaniesResponse {
  companies: CompanyListItem[];
}

export interface UserCompanyChip {
  id: string;
  legalName: string;
  shortName: string | null;
  isManager: boolean;
  /** Wszystkie role osoby w firmie, w kolejności — pierwsza = główna (ADR-0103). */
  roles?: Array<{ id: string; name: string }>;
  /** Rola główna (pierwsza z `roles`) — dla ekranów sprzed wielu ról. */
  roleTemplateId: string | null;
  roleTemplateName: string | null;
}

/**
 * Wiersz listy użytkowników. UWAGA (silnik list, tryb projekcji `?columns=`): lista `Users.tsx`
 * ZAWSZE pobiera projekcję, więc realnie GWARANTOWANE na każdym wierszu są tylko: `id`, `gcrecord`
 * oraz pola z `alwaysSlugs` strony (`email`, `userType`, `status`, `isSuperUser`, `companies`) i
 * kolumny aktualnie widoczne. `defaultCompanyId`/`hasAccount` NIE są w `alwaysSlugs` ani nie mają
 * kolumny — pod projekcją listy będą `undefined` (mimo typu); niepuste są tylko na wierszu z drawera
 * (`userDetailToListItem`, osobny fetch szczegółu). Typ trzyma je nie-opcjonalne dla parytetu z tym
 * drugim źródłem — nie polegaj na nich, czytając wiersz z listy (mirror `InvoiceRow`, `lib/documents/purchase-invoice-columns.tsx`).
 */
export interface UserListItem {
  id: string;
  email: string;
  /**
   * `users.username` w DB jest `NOT NULL`, a trasa listy zawsze go zwraca — ale typ jest
   * opcjonalny, bo `userDetailToListItem` (Users.tsx) buduje wiersz z odpowiedzi szczegółu,
   * która NIE zwraca username do Etapu 1.4b (jawny komentarz w UserDetail.tsx).
   */
  username?: string;
  displayName: string | null;
  userType: 'human' | 'api' | 'service' | 'impersonation';
  status: 'active' | 'suspended' | 'invited';
  isSuperUser: boolean;
  defaultCompanyId: string | null;
  hasAccount: boolean;
  companies: UserCompanyChip[];
  lastLoginAt: string | null;
  createdAt: string;
  /** Niepuste tylko gdy lista pobrana z `?include_deleted=true`. */
  gcrecord: string | null;
  /** Grupy rekordu — kody słownika `user_groups`, wiele na rekord (filtr chipów listy). */
  groups?: string[];
  /**
   * Klucz avatara w MinIO (`users.avatar_path`) — dociągany jako companion kolumny
   * „display_name" (`kartoteka-listify` użytkownicy). Serwowanie pełnego URL (presigned)
   * to follow-up poza zakresem tej migracji (patrz `MyProfile.tsx`) — dziś pole niesie
   * WYŁĄCZNIE surowy klucz obiektu, nie gotowy do `<img src>`.
   */
  avatarPath?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze listy). Kolumna RĘCZNA. */
  categoryName?: string | null;
  /** Wartości pól dodatkowych (ext) — obecne WYŁĄCZNIE w trybie projekcji `?columns=`. */
  ext?: Record<string, unknown>;
}

export interface UsersListResponse {
  users: UserListItem[];
  total: number;
  limit: number;
  offset: number;
  /**
   * Etykiety pól `reference` (sibling do wierszy) — obecne gdy `?columns=` obejmuje
   * przynajmniej jedno takie pole. Patrz `ExtRefLabels` (`@/lib/ext-list-columns`).
   */
  extRefLabels?: ExtRefLabels;
  /**
   * Slugi kolumn CORE sortowalnych/filtrowalnych serwerowo (silnik list) — patrz
   * `useServerListControls`. Pole ADDYTYWNE (konsumenci spoza `Users.tsx` mogą je
   * ignorować bez zmiany zachowania).
   */
  meta?: ListResponseMeta;
}

export interface UserCompanyAccess {
  companyId: string;
  legalName: string;
  shortName: string | null;
  /** Wszystkie role osoby w tej firmie, w kolejności — pierwsza = główna (ADR-0103). */
  roles?: Array<{ id: string; name: string; roleKey?: string }>;
  /** Rola główna (pierwsza z `roles`) — dla ekranów sprzed wielu ról. */
  roleTemplateId: string | null;
  roleTemplateName: string | null;
  isManager: boolean;
  grantedPermissions: string[];
}

export interface UserPermissionsResponse {
  userId: string;
  companies: UserCompanyAccess[];
}

/** Odwrotny widok `UserCompanyAccess` — jeden user w kontekscie JEDNEJ firmy (widget "Uzytkownicy firmy"). */
export interface CompanyMember {
  userId: string;
  email: string;
  displayName: string | null;
  userType: 'human' | 'api' | 'service' | 'impersonation';
  status: 'active' | 'suspended' | 'invited';
  isSuperUser: boolean;
  /** Wszystkie role osoby w firmie, w kolejności (pierwsza = główna; ADR-0103). */
  roles?: Array<{ id: string; name: string }>;
  /** Rola główna — dla klientów sprzed wielu ról. */
  roleTemplateId: string | null;
  roleTemplateName: string | null;
  isManager: boolean;
}

export interface CompanyMembersResponse {
  companyId: string;
  members: CompanyMember[];
  total: number;
  limit: number;
  offset: number;
}

export interface RoleTemplate {
  id: string;
  /** Firma, do której należy rola (role są per firma). */
  companyId?: string | null;
  /** Linia rodowa — ta sama rola w różnych firmach ma różne `id`, wspólny `roleKey`. */
  roleKey?: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isDefaultAdmin: boolean;
  isDefaultMember: boolean;
  permissionCodes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RoleTemplatesResponse {
  roleTemplates: RoleTemplate[];
}

// ============================================================================
// Query keys
// ============================================================================

export const PERMISSION_DEFINITIONS_KEY = ['permissions', 'definitions'] as const;
export const COMPANIES_KEY = ['companies', 'list'] as const;
export const USERS_LIST_KEY = ['users', 'list'] as const;
export const ROLE_TEMPLATES_KEY = ['role-templates', 'list'] as const;
export const USER_PERMISSIONS_KEY = (userId: string) => ['users', userId, 'permissions'] as const;
export const COMPANY_MEMBERS_KEY = (companyId: string) =>
  ['companies', companyId, 'members'] as const;

// ============================================================================
// Hooks
// ============================================================================

export function usePermissionDefinitions() {
  return useQuery<PermissionDefinitionsResponse>({
    queryKey: PERMISSION_DEFINITIONS_KEY,
    queryFn: async () => {
      const res = await api.get<PermissionDefinitionsResponse>('/permissions/definitions');
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useCompanies() {
  return useQuery<CompaniesResponse>({
    queryKey: COMPANIES_KEY,
    queryFn: async () => {
      // Picker firm — potrzebuje pełnej listy. Od migracji `/companies` na silnik list
      // odpowiedź jest paginowana (domyślny `limit=50`), więc jawnie prosimy o górny clamp
      // silnika (`limit=200`). ZNANY DŁUG: tenant z >200 firmami cicho urwie nadmiar — firm per
      // tenant jest zwykle mało; przy realnym przekroczeniu picker przejdzie na serwerowe `?q=`.
      const res = await api.get<CompaniesResponse>('/companies?limit=200');
      return res.data;
    },
    staleTime: 30_000,
  });
}

export interface UsersListOptions<TData = UsersListResponse> {
  /**
   * Własna reguła ponawiania nieudanego pobrania (TanStack `retry`). Pominięta = domyślna klienta.
   * Dla list opcji filtra: `retryUnlessForbidden` — odmowa 403 (brak prawa do listy użytkowników)
   * nie zmieni się po ponowieniu, więc nie ponawiamy jej.
   */
  retry?: (failureCount: number, error: unknown) => boolean;
  /**
   * Czy zapytanie w ogóle ma być wysłane (TanStack `enabled`). Pominięte = zawsze (jak dotąd).
   * Dla list opcji filtra: `false`, gdy użytkownik nie ma prawa podglądu użytkowników — inaczej
   * każde otwarcie listy kończyłoby się odmową 403 (bez danych TanStack ponawia pobranie przy
   * każdym montażu).
   */
  enabled?: boolean;
  /**
   * Projekcja odpowiedzi (TanStack `select`) — wynik hooka to jej wartość. Wyjątek rzucony z
   * projekcji staje się błędem zapytania (`isError`, `error`), a nie wywala renderu. Referencja
   * MUSI być stabilna (funkcja modułowa), inaczej projekcja liczy się przy każdym renderze.
   */
  select?: (data: UsersListResponse) => TData;
  /**
   * Gdy true → `?includeArchived=true` (alias `includeDeleted` po stronie BE).
   * Wymaga permission `users.view_deleted`. Lista zawiera wtedy soft-deleted.
   */
  includeDeleted?: boolean;
  /** Silnik list: default 50, max 200. Jesli pominiete → default silnika. */
  limit?: number;
  /** Offset paginacji serwerowej (silnik list) — mirror `EmployeesListFilters.offset`. */
  offset?: number;
  /**
   * Filtr po GRUPACH ze słownika `user_groups` (chipy nad listą, projekt „grupy").
   * Wiele kodów = OR („ma którąkolwiek z"). Mirror `EmployeesListFilters.groups`.
   */
  groups?: string[];
  /**
   * Wyszukiwarka pełnotekstowa (multi-word ILIKE). Emitowana jako `?search=` — deskryptor listy
   * użytkowników obsługuje ją w `buildAdHocFilters` (nie `?q=` silnika), więc kontrakt drutowy
   * frontu (ten hook, UserPicker, adapter `users`) pozostaje bez zmian. Mirror pracowników.
   */
  search?: string;
  /** F12 PR2 cz.5.1 — CSV slugów kolumn (np. ['email','display_name','username']). */
  searchColumns?: string[];
  /** F12 PR2 cz.5.1 — slug pola sortującego (whitelist backendu). */
  sort?: string;
  /** F12 PR2 cz.5.1 — kierunek sortu. */
  order?: 'asc' | 'desc';
  /** Kreator Listy — JSON-stringified tablica filtrów per kolumna (nagłówek + presety). */
  headerFilters?: string;
  /**
   * Company-scope opcjonalny (Etap 5 — `useActiveCompanyScope`/`companyIdParam`).
   * Brak => Model B: unia po wszystkich firmach widocznych dla usera (backend
   * `resolveListCompanyScope`). Podany => zawężenie do jednej firmy (403 gdy user
   * nie ma do niej dostępu, 400 gdy nie-UUID).
   */
  companyId?: string;
  /**
   * Kreator kolumn ext — CSV slugów widocznych kolumn dla `?columns=` projekcji
   * (kontrakt `GET /api/users?columns=` — core-slugi z `CORE_COLUMN_WHITELIST` w
   * `apps/api/app/api/users/route.ts` + ext-pola aktywne w module `users`). Mirror
   * `EmployeesListFilters.columns` w `use-tenant-people.ts`. Gdy podane, odpowiedź
   * niesie `row.ext` dla żądanych pól dodatkowych (`UserListItem.ext`).
   */
  columns?: string[];
}

/** Filtry listy użytkowników (bez stronicowania i projekcji kolumn). */
export type UsersListFilterOptions = Pick<
  UsersListOptions,
  | 'includeDeleted'
  | 'search'
  | 'searchColumns'
  | 'sort'
  | 'order'
  | 'headerFilters'
  | 'companyId'
  | 'groups'
>;

/**
 * Parametry filtrów listy użytkowników BEZ `limit`/`offset`/`columns` — wspólne dla zapytania
 * listy (`useUsersList`) i eksportu widoku listy do CSV (E16, `GET /api/list-export/users`), żeby
 * plik zawierał dokładnie to, co lista.
 */
export function buildUsersListFilterParams(options: UsersListFilterOptions = {}): URLSearchParams {
  const { includeDeleted, search, searchColumns, sort, order, headerFilters, companyId, groups } =
    options;
  const params = new URLSearchParams();
  if (includeDeleted) params.set('includeArchived', 'true');
  if (search) params.set('search', search);
  if (searchColumns && searchColumns.length > 0)
    params.set('searchColumns', searchColumns.join(','));
  if (sort) params.set('sort', sort);
  if (order) params.set('order', order);
  if (headerFilters) params.set('headerFilters', headerFilters);
  if (companyId) params.set('companyId', companyId);
  // Powtórzony parametr, nie lista po przecinku — backend czyta `searchParams.getAll('groups')`.
  for (const code of groups ?? []) params.append('groups', code);
  return params;
}

export function useUsersList<TData = UsersListResponse>(options: UsersListOptions<TData> = {}) {
  const includeDeleted = options.includeDeleted ?? false;
  const limit = options.limit;
  const offset = options.offset;
  const { search, searchColumns, sort, order, headerFilters, companyId, columns } = options;
  const groups = options.groups;

  // Backward compat: bez opcji → URL bez query (`/users`). UsersPermissionsTab
  // (T10.10) wywoluje hook bez argumentow i mockuje `/users` exact.
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));
  const filterParams = buildUsersListFilterParams({
    includeDeleted,
    search,
    searchColumns,
    sort,
    order,
    headerFilters,
    companyId,
    groups,
  });
  for (const [key, value] of filterParams) params.append(key, value);
  if (columns && columns.length > 0) params.set('columns', columns.join(','));
  const query = params.toString();
  const url = query ? `/users?${query}` : '/users';

  const hasAnyOption =
    includeDeleted ||
    limit !== undefined ||
    offset !== undefined ||
    search ||
    sort ||
    !!headerFilters ||
    !!companyId ||
    (columns && columns.length > 0) ||
    (groups && groups.length > 0);
  return useQuery<UsersListResponse, Error, TData>({
    queryKey: hasAnyOption
      ? ([
          ...USERS_LIST_KEY,
          {
            includeDeleted,
            limit: limit ?? null,
            offset: offset ?? null,
            groups: groups && groups.length > 0 ? [...groups].sort() : null,
            search: search ?? null,
            searchColumns: searchColumns ?? null,
            sort: sort ?? null,
            order: order ?? null,
            headerFilters: headerFilters ?? null,
            companyId: companyId ?? null,
            columns: columns ?? null,
          },
        ] as const)
      : USERS_LIST_KEY,
    queryFn: async () => {
      const res = await api.get<UsersListResponse>(url);
      return res.data;
    },
    staleTime: 30_000,
    // Tylko gdy podane: jawne `retry: undefined` nadpisałoby domyślne ponawianie klienta.
    ...(options.retry !== undefined ? { retry: options.retry } : {}),
    ...(options.select !== undefined ? { select: options.select } : {}),
    ...(options.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

export function useUserPermissions(userId: string | null) {
  return useQuery<UserPermissionsResponse>({
    queryKey: userId ? USER_PERMISSIONS_KEY(userId) : ['users', 'none', 'permissions'],
    queryFn: async () => {
      const res = await api.get<UserPermissionsResponse>(`/users/${userId}/permissions`);
      return res.data;
    },
    enabled: !!userId,
    staleTime: 0, // chcemy widziec efekt zapisu natychmiast
  });
}

/** Lista userow z dostepem do jednej firmy + ich rola (widget "Uzytkownicy firmy" na karcie firmy). */
export function useCompanyMembers(companyId: string | null) {
  return useQuery<CompanyMembersResponse>({
    queryKey: companyId ? COMPANY_MEMBERS_KEY(companyId) : ['companies', 'none', 'members'],
    queryFn: async () => {
      // limit=100 (MAX_LIMIT backendu) — widget nie ma jeszcze UI paginacji, wiec
      // prosimy o gorna granice zamiast domyslnych 25, zeby typowa firma zmiescila
      // sie w jednej stronie. Firma z >100 czlonkow zobaczy tylko pierwszych 100
      // (przyszly follow-up: paginacja w widgecie, patrz `total` w odpowiedzi).
      const res = await api.get<CompanyMembersResponse>(
        `/companies/${companyId}/members?limit=100`,
      );
      return res.data;
    },
    enabled: !!companyId,
    staleTime: 0, // chcemy widziec efekt zapisu natychmiast
  });
}

export function useRoleTemplates() {
  return useQuery<RoleTemplatesResponse>({
    queryKey: ROLE_TEMPLATES_KEY,
    queryFn: async () => {
      const res = await api.get<RoleTemplatesResponse>('/role-templates');
      return res.data;
    },
    staleTime: 30_000,
  });
}
